import { ng } from "@common/ngds";
import { Drawer, MinMax, PathBuilder, Point, StyleForRect } from "@common/components/Rendering";
import { ArrowId } from "../ViewportStyles";

// Shared lookup: the section database entry for the first beam segment of the first (and only)
// element/structure a column project has. Used by every function in this file that needs the
// cross-section polyline.
const getFirstSection = (projectData: ng.frontend.column.Project | null | undefined) => {
  const first_element = projectData?.data?.input?.structures?.[0]?.elements[0];
  const first_segment = first_element?.getBeam()?.segments?.[0];
  const secGuid = first_segment?.layer?.start?.secGuid;
  return projectData?.data?.input?.secDb.find((x) => x.id?.guid === secGuid);
};

export const shouldUseFakeSilhouetteForSection = (
  projectData: ng.frontend.column.Project | null | undefined,
): boolean => {
  switch (getFirstSection(projectData)?.type) {
    // circular section types shouldn't display the fake silhouette
    case ng.sections.SectionType.SECTION_TYPE_C:
    case ng.sections.SectionType.SECTION_TYPE_KCKR:
    case ng.sections.SectionType.SECTION_TYPE_VCKR:
    case ng.sections.SectionType.SECTION_TYPE_CHS:
      return false;
    default:
      return true;
  }
};

const renderingPointsFromPoints = (points: ng.geometry.Point2D[]) => {
  return points.map((pp) => [pp.x, -pp.y] as Point);
};

const drawShape = (path: PathBuilder, points: Point[]) => {
  let first = true;
  for (const p of points) {
    if (first) path.moveTo(p);
    else path.drawLineTo(p);
    first = false;
  }
};

interface MinMaxVal {
  min: number;
  max: number;
}
const minmax = (m: MinMaxVal, v: number): MinMaxVal => {
  return {
    min: Math.min(m.min, v),
    max: Math.max(m.max, v),
  };
};
export interface Extent {
  hor: MinMaxVal;
  ver: MinMaxVal;
}

const extentFromPoints = (points: Point[]): Extent | null => {
  return points.reduce((old: null | Extent, p): Extent => {
    const [hor, ver] = p;
    if (old === null) {
      return {
        hor: { min: hor, max: hor },
        ver: { min: ver, max: ver },
      };
    }
    return {
      hor: minmax(old.hor, hor),
      ver: minmax(old.ver, ver),
    };
  }, null);
};

export const drawGeomCrossSection = (
  g: Drawer<ArrowId>,
  projectData: ng.frontend.column.Project,
  geom_style: StyleForRect,
) => {
  const sec = getFirstSection(projectData);
  const poly = sec?.polylines;
  const sourcePoints = poly?.perimeter?.points;

  if (sourcePoints) {
    const shape = new PathBuilder();

    const points = renderingPointsFromPoints(sourcePoints);
    drawShape(shape, points);

    const holes = poly?.holes ?? [];
    for (const hole of holes) {
      const hp = renderingPointsFromPoints(hole.points);
      drawShape(shape, hp);
    }

    g.drawPath(geom_style, shape);

    return extentFromPoints(points) ?? null;
  }

  return null;
};

export const calculateGeomCrossSection = (projectData: ng.frontend.column.Project) => {
  const sec = getFirstSection(projectData);
  const poly = sec?.polylines;
  const sourcePoints = poly?.perimeter?.points;

  if (sourcePoints) {
    const points = renderingPointsFromPoints(sourcePoints);
    return extentFromPoints(points) ?? null;
  }

  return null;
};

interface CrossSectionPoints {
  perimeter: Point[];
  holes: Point[][];
}

// Raw cross-section outline points (perimeter + holes), for callers that need the actual shape
// rather than a 2D drawable or its extent - e.g. extruding the section along the column height
// for the 3D view. Unlike renderingPointsFromPoints (used for the 2D cross-section drawing,
// where the y axis is flipped for screen space), the y coordinate is NOT negated here: it is
// returned as sec-local (x, y), matching the convention already used for reinforcement bar
// points (see drawLongitudalReinf.ts), which are mapped directly to world y/z with no sign flip.
export const getGeomCrossSectionPoints = (
  projectData: ng.frontend.column.Project | null | undefined,
): CrossSectionPoints | null => {
  const sec = getFirstSection(projectData);
  const poly = sec?.polylines;
  const sourcePoints = poly?.perimeter?.points;

  if (!sourcePoints) {
    return null;
  }

  const perimeter = withWinding(
    sourcePoints.map((pp) => [pp.x, pp.y] as Point),
    true,
  );
  const holes = (poly?.holes ?? []).map((hole) =>
    withWinding(
      hole.points.map((pp) => [pp.x, pp.y] as Point),
      false,
    ),
  );

  return { perimeter, holes };
};

// The 3D views derive side-face normals from winding: counter-clockwise gives outward normals, so
// the perimeter must be counter-clockwise and holes clockwise (normals pointing into the void).
const withWinding = (points: Point[], counterClockwise: boolean): Point[] => {
  const signedArea = points.reduce((total, [x, y], index) => {
    const [nextX, nextY] = points[(index + 1) % points.length];
    return total + x * nextY - nextX * y;
  }, 0);
  return signedArea > 0 === counterClockwise ? points : [...points].reverse();
};
